import { Box, CircularProgress, Modal, Typography } from "@mui/material";
import TrashIcon from "../../icons/TrashIcon";
import { useTranslation } from "react-i18next";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

export default function ConfirmDeleteDialog({
  open,
  isNote,
  deleting = false,
  onConfirm,
  onCancel
}: {
  open: boolean;
  isNote: boolean;
  deleting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={deleting ? undefined : onCancel}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 420 },
          backgroundColor: semanticColors.surface.canvas,
          borderRadius: "20px",
          boxShadow: "0px 8px 40px rgba(30,39,79,0.18)",
          p: "28px",
          outline: "none"
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: semanticColors.feedback.dangerBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: "16px"
          }}
        >
          <TrashIcon size={22} color={semanticColors.feedback.danger} />
        </Box>

        <Typography
          sx={{
            ...typography.heading.s,
            color: semanticColors.text.primary,
            mb: "8px"
          }}
        >
          {isNote
            ? t("clinicalRecords.deleteNoteTitle")
            : t("clinicalRecords.deleteSummaryTitle")}
        </Typography>

        <Typography
          sx={{
            ...typography.body.l,
            color: semanticColors.text.secondary,
            mb: "24px"
          }}
        >
          {t("clinicalRecords.deleteConfirmMessage")}
        </Typography>

        <Box sx={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <Box
            component="button"
            onClick={onCancel}
            disabled={deleting}
            sx={{
              px: "24px",
              height: "44px",
              borderRadius: "40px",
              border: `1.5px solid ${semanticColors.border.strong}`,
              bgcolor: "transparent",
              cursor: deleting ? "not-allowed" : "pointer",
              ...typography.body.lBold,
              color: semanticColors.text.primary,
              "&:hover": { bgcolor: semanticColors.surface.containerLow },
              "&:disabled": { opacity: 0.6 }
            }}
          >
            {t("clinicalRecords.cancel")}
          </Box>
          <Box
            component="button"
            onClick={onConfirm}
            disabled={deleting}
            sx={{
              px: "24px",
              minWidth: "132px",
              height: "44px",
              borderRadius: "40px",
              border: "none",
              bgcolor: semanticColors.feedback.danger,
              cursor: deleting ? "not-allowed" : "pointer",
              ...typography.body.lBold,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": { opacity: 0.88 },
              "&:disabled": { opacity: 0.8 }
            }}
          >
            {deleting ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              t("clinicalRecords.confirmDelete")
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
